import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { supabase } from "./supabase";
import { roundCurrency, GST_RATE } from "./claimFinancials";
import { contractValueFromSubtotal } from "./claimProgressFromSubtotal";
import { drawPaymentClaimPage1 } from "./drawClaimSummaryPage1";
import {
  extractPage1Summary,
  hasDeductionSplit,
  type Page1Summary,
} from "./page1Summary";

// ─────────────────────────────────────────────────────────────────────────────
// Separate edit-time PDF regeneration.
//
// This is intentionally INDEPENDENT of the Send Report path
// (generate-progress-claim). When a claim is edited and saved, the original
// stored PDF still shows the values it was generated with. This rebuilds ONLY
// the page-1 financial summary (TAX INVOICE) from the edited claim figures,
// using the same before-GST retention and release math as the rest of the app,
// and keeps
// every following page of the existing PDF (contract breakdown, variation forms,
// statutory declaration) untouched, so appended documents are never lost.
// ─────────────────────────────────────────────────────────────────────────────

export interface RegenReport {
  id: string;
  project_id: string;
  claim_number: number | null;
  claim_month: number;
  claim_year: number;
  variations_this_month: number | null;
  variations_outstanding: number | null;
  subtotal: number | null;
  claim_this_month: number | null;
  retention_amount: number | null;
  retention_release_amount: number | null;
  retention_release_gst: number | null;
  retention_release_total_inc_gst: number | null;
  total_exc_gst: number | null;
  total_inc_gst: number | null;
  // claimed_set is jsonb: normally an object, but a caller could hand us a raw
  // JSON string, so the new-path detection parses defensively (see below).
  claimed_set?: {
    install_hire_this_month?: number | null;
    // SEQ-218: the part of install_hire_this_month re-claimed from earlier
    // claims; the subtotal excludes it. Absent on older claims (reads as 0).
    install_hire_reclaimed?: number | null;
    hire_billing?: unknown[] | null;
    // SEQ-145: frozen top-down page-1 figures. Present only on new-format
    // reports; its presence switches regeneration to the new page-1 layout
    // (shape + Amendment 2 split detection live in page1Summary.ts).
    summary?: Page1Summary | null;
  } | null;
  submission_date: string | null;
  payment_due_date: string | null;
  pdf_url: string | null;
  pdf_filename: string | null;
}

interface ProjectInfo {
  builder_name?: string | null;
  builder_address?: string | null;
  job_location?: string | null;
  job_address?: string | null;
}

interface SettingsInfo {
  company_name?: string | null;
  company_address?: string | null;
  company_city?: string | null;
  company_abn?: string | null;
  company_phone?: string | null;
  contact_email?: string | null;
  contract_description?: string | null;
  bank_name?: string | null;
  bank_bsb?: string | null;
  bank_account?: string | null;
  bank_account_name?: string | null;
}

// Local formatter, deliberately independent of formatCurrency, which masks
// values ("•••••") when Safe Mode is active. The PDF must always carry the real
// figures regardless of the on-screen Safe Mode toggle.
const amountFormatter = new Intl.NumberFormat("en-AU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmt = (amount: number | string) =>
  typeof amount === "number" ? amountFormatter.format(amount) : amount;

const formatDate = (value: string | null): string =>
  value
    ? new Date(value).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "TBD";

// Page-1 drawing (drawPaymentClaimPage1, Page1Data and its currency formatter) moved
// to drawClaimSummaryPage1.ts (Sep 2026 relayout hotfix) - kept pure and free
// of this file's Supabase import so it can also render under Deno (see
// scripts/renderClaimSummaryPreview.ts). Summary extraction + Amendment 2
// layout selection live in page1Summary.ts
// (pure module, deno-tested).

/**
 * Rebuild the page-1 financial summary from the edited claim and re-merge it
 * with the existing PDF's remaining pages. Returns the new public PDF URL, or
 * null if there is no existing PDF to regenerate from.
 */
export async function regenerateClaimSummaryPdf(
  report: RegenReport,
): Promise<{ pdfUrl: string; pageCount: number } | null> {
  if (!report.pdf_url) return null; // Nothing to re-merge into.

  // ── Gather the static content (same sources the Send Report path uses) ──
  // claims_settings is a key/value table (setting_key/setting_value), so it is
  // pivoted into an object exactly like generate-progress-claim does.
  const [{ data: project }, { data: settingsRows }, existingBytes, logoBytes] =
    await Promise.all([
      supabase
        .from("claims_projects")
        .select("builder_name, builder_address, job_location, job_address")
        .eq("id", report.project_id)
        .single(),
      supabase.from("claims_settings").select("setting_key, setting_value"),
      fetch(report.pdf_url).then((r) => r.arrayBuffer()),
      fetch("/SEQ_Logo.png").then((r) => r.arrayBuffer()),
    ]);

  const proj = (project ?? {}) as ProjectInfo;
  const cfg = ((settingsRows ?? []) as Array<{ setting_key: string; setting_value: unknown }>).reduce(
    (acc, row) => {
      acc[row.setting_key] = row.setting_value as string | null;
      return acc;
    },
    {} as SettingsInfo & Record<string, string | null>,
  );

  // ── Recompute figures from the edited row (retention before GST) ──
  const subtotal = roundCurrency(
    Number(report.subtotal ?? report.claim_this_month ?? 0),
  );
  const variationsThisMonth = roundCurrency(Number(report.variations_this_month ?? 0));
  const variationsOutstanding = roundCurrency(Number(report.variations_outstanding ?? 0));
  const installHireThisMonth = roundCurrency(
    Number(report.claimed_set?.install_hire_this_month ?? 0),
  );
  // Progress portion (Contract Value as per breakdown) = subtotal minus variations.
  // Guard against a manually-unlocked Claim This Month that excludes variations,
  // which could otherwise render a negative contract value.
  const contractValue = contractValueFromSubtotal({
    subtotal,
    variationsThisMonth,
    variationsOutstanding,
    installHireThisMonth,
    installHireReclaimed: report.claimed_set?.install_hire_reclaimed,
  });
  const claimThisMonth = subtotal;
  const retentionAmount = roundCurrency(Number(report.retention_amount ?? 0));
  const retentionReleaseAmount = roundCurrency(Number(report.retention_release_amount ?? 0));
  const subtotalExRetentionHeld = roundCurrency(Math.max(0, claimThisMonth - retentionAmount));
  const subtotalIncRetentionRelease = roundCurrency(subtotalExRetentionHeld + retentionReleaseAmount);
  const gstAmount = roundCurrency(subtotalIncRetentionRelease * GST_RATE);
  const subtotalIncGst = roundCurrency(subtotalIncRetentionRelease + gstAmount);
  const totalPayable = roundCurrency(
    Number(report.total_inc_gst ?? subtotalIncGst),
  );

  // ── Load existing PDF; build the new summary page at the same page size ──
  const existingDoc = await PDFDocument.load(existingBytes, { updateMetadata: false });
  const existingPages = existingDoc.getPageIndices();
  const firstSize = existingDoc.getPage(0).getSize();

  const finalDoc = await PDFDocument.create();
  const page = finalDoc.addPage([firstSize.width, firstSize.height]);
  const { width, height } = page.getSize();

  const logoImage = await finalDoc.embedPng(logoBytes);
  const font = await finalDoc.embedFont(StandardFonts.Helvetica);
  const fontBold = await finalDoc.embedFont(StandardFonts.HelveticaBold);

  const drawText = (
    text: string,
    x: number,
    y: number,
    size = 10,
    options: { bold?: boolean; align?: "left" | "right" | "center"; color?: ReturnType<typeof rgb> } = {},
  ) => {
    const textFont = options.bold ? fontBold : font;
    const textWidth = textFont.widthOfTextAtSize(text, size);
    let xPos = x;
    if (options.align === "right") xPos = x - textWidth;
    else if (options.align === "center") xPos = x - textWidth / 2;
    page.drawText(text, {
      x: xPos,
      y: height - y,
      size,
      font: textFont,
      color: options.color || rgb(0, 0, 0),
    });
  };

  const drawLine = (x1: number, y1: number, x2: number, y2: number, thickness = 1) => {
    page.drawLine({
      start: { x: x1, y: height - y1 },
      end: { x: x2, y: height - y2 },
      thickness,
      color: rgb(0, 0, 0),
    });
  };

  // ── SEQ-145 dual path ──
  // New-format reports carry claimed_set.summary (frozen A/B/C etc.); regenerate
  // the ported PAYMENT CLAIM SUMMARY page from those figures. Legacy reports have
  // no summary and keep the original TAX INVOICE drawing below, byte-for-byte.
  const summary = extractPage1Summary(report.claimed_set);
  if (summary) {
    // Original contract value: mirror the edge function's netContractSum source
    // (index.ts:781-835): gross = subcategory amounts, plus custom-amount
    // categories' custom_amount (skipping their subcategories), minus discounts.
    const [{ data: contractCats }, { data: contractDiscounts }] = await Promise.all([
      supabase
        .from("claims_categories")
        .select("id, custom_amount, is_custom_amount_only, claims_subcategories(amount)")
        .eq("project_id", report.project_id),
      supabase.from("claims_discounts").select("amount").eq("project_id", report.project_id),
    ]);

    const catRows = (contractCats ?? []) as Array<{
      is_custom_amount_only: boolean | null;
      custom_amount: number | null;
      claims_subcategories: Array<{ amount: number | null }> | null;
    }>;
    let grossContractSum = 0;
    for (const cat of catRows) {
      if (cat.is_custom_amount_only) {
        grossContractSum += Number(cat.custom_amount ?? 0);
      } else {
        for (const s of cat.claims_subcategories ?? []) {
          grossContractSum += Number(s.amount ?? 0);
        }
      }
    }
    const totalDiscount = ((contractDiscounts ?? []) as Array<{ amount: number | null }>).reduce(
      (sum, d) => sum + Number(d.amount ?? 0),
      0,
    );
    const netContractSum = grossContractSum - totalDiscount;
    const approvedVariations = roundCurrency(Number(summary.paid_variations_total ?? 0));

    // Totals reflect the edited row, not a recomputation of A/B/C.
    const cValue = roundCurrency(Number(summary.C ?? 0));
    const netForGst = roundCurrency(
      Math.max(0, cValue) - retentionAmount + retentionReleaseAmount,
    );
    const storedExcGst = report.total_exc_gst;
    const storedIncGst = report.total_inc_gst;
    const totalInc =
      storedIncGst != null
        ? roundCurrency(Number(storedIncGst))
        : roundCurrency(netForGst * (1 + GST_RATE));
    const gstValue =
      storedIncGst != null && storedExcGst != null
        ? roundCurrency(Number(storedIncGst) - Number(storedExcGst))
        : roundCurrency(netForGst * GST_RATE);

    // Whole days between submission and due dates; null (rendered "-") if either
    // is missing.
    const claimMs = report.submission_date ? Date.parse(report.submission_date) : NaN;
    const dueMs = report.payment_due_date ? Date.parse(report.payment_due_date) : NaN;
    const paymentPeriodDays =
      Number.isFinite(claimMs) && Number.isFinite(dueMs)
        ? Math.round((dueMs - claimMs) / (24 * 60 * 60 * 1000))
        : null;

    drawPaymentClaimPage1(page, font, fontBold, logoImage, {
      companyName: cfg.company_name || "SEQ FORMWORK PTY LTD",
      companyAddress: cfg.company_address || "63 Ageston Road",
      companyCity: cfg.company_city || "Alberton Qld 4207",
      companyABN: cfg.company_abn || "28 168 628 591",
      companyPhone: cfg.company_phone || "(07) 55461167",
      companyEmail: cfg.contact_email || "admin@seqformwork.com.au",
      builderName: proj.builder_name || "",
      claimNumber: report.claim_number ?? "",
      projectName: proj.job_location || "",
      siteAddress: proj.job_address || "",
      contractDescription:
        cfg.contract_description || "Supply and Installation of formwork as per contract",
      claimDateStr: formatDate(report.submission_date),
      paymentDueStr: formatDate(report.payment_due_date),
      paymentPeriodDays,
      originalContractValue: roundCurrency(netContractSum),
      approvedVariations,
      revisedContractValue: roundCurrency(netContractSum + approvedVariations),
      A: roundCurrency(Number(summary.A ?? 0)),
      B: roundCurrency(Number(summary.B ?? 0)),
      bReceipts: hasDeductionSplit(summary)
        ? roundCurrency(Number(summary.b_receipts))
        : undefined,
      retentionHeldToDate: hasDeductionSplit(summary)
        ? roundCurrency(Number(summary.retention_held_to_date))
        : undefined,
      accumulatedRetention:
        summary.accumulated_retention != null
          ? roundCurrency(Number(summary.accumulated_retention))
          : undefined,
      retentionCeiling:
        summary.retention_ceiling != null
          ? roundCurrency(Number(summary.retention_ceiling))
          : undefined,
      retentionCeilingPercent:
        summary.retention_ceiling_percent != null
          ? Number(summary.retention_ceiling_percent)
          : undefined,
      C: cValue,
      P: contractValue,
      installHire: installHireThisMonth,
      V: roundCurrency(variationsThisMonth + variationsOutstanding),
      R: retentionAmount,
      release: retentionReleaseAmount,
      G: gstValue,
      T: totalInc,
      bankName: cfg.bank_name || "Commonwealth Bank",
      bsb: cfg.bank_bsb || "064 - 474",
      account: cfg.bank_account || "10563659",
      accountName: cfg.bank_account_name || "SEQ Formwork Pty Ltd",
    });
  }

  // ── Legacy TAX INVOICE page (unchanged; drawn only when no summary) ──
  if (!summary) {
  // ── Header ──
  const margin = 50;
  let yPos = 40;

  const logoScale = 180 / logoImage.width;
  const scaledLogo = { width: 180, height: logoImage.height * logoScale };
  page.drawImage(logoImage, {
    x: margin,
    y: height - yPos - scaledLogo.height,
    width: scaledLogo.width,
    height: scaledLogo.height,
  });

  const companyName = cfg.company_name || "SEQ FORMWORK PTY LTD";
  const companyAddress = cfg.company_address || "63 Ageston Road";
  const companyCity = cfg.company_city || "Alberton Qld 4207";
  const companyABN = cfg.company_abn || "28 168 628 591";
  const companyPhone = cfg.company_phone || "(07) 55461167";
  const companyEmail = cfg.contact_email || "admin@seqformwork.com.au";

  let headerY = yPos;
  drawText(companyName, width - margin, headerY, 12, { bold: true, align: "right" });
  headerY += 14;
  drawText(companyAddress, width - margin, headerY, 9, { align: "right" });
  headerY += 11;
  drawText(companyCity, width - margin, headerY, 9, { align: "right" });
  headerY += 11;
  drawText(`Phone: ${companyPhone}`, width - margin, headerY, 9, { align: "right" });
  headerY += 14;
  drawText(`email: ${companyEmail}`, width - margin, headerY, 9, { align: "right" });
  headerY += 11;
  drawText(`ABN: ${companyABN}`, width - margin, headerY, 9, { align: "right" });

  yPos += Math.max(scaledLogo.height, 80) + 10;

  drawText("TAX INVOICE", width - margin, yPos, 12, { bold: true, align: "right" });
  yPos += 30;
  drawLine(margin, yPos, width - margin, yPos, 0.5);
  yPos += 20;

  // ── To: ──
  drawText("To:", margin, yPos, 10, { bold: true });
  drawText(proj.builder_name || "Builder Name", margin + 40, yPos, 10, { bold: true });
  yPos += 14;
  drawText(proj.builder_address || "Builder Address", margin + 40, yPos, 10, { bold: true });
  yPos += 25;

  // ── Project & Contract ──
  drawText("Project :", margin, yPos, 10, { bold: true });
  drawText(proj.job_location || "Project Name", margin + 55, yPos, 10, { bold: true });
  yPos += 14;
  if (proj.job_address) {
    drawText(proj.job_address, margin + 55, yPos, 10, { bold: true });
    yPos += 14;
  }
  drawText("Contract:", margin, yPos, 10, { bold: true });
  drawText(
    cfg.contract_description || "Supply and Installation of formwork as per contract",
    margin + 55,
    yPos,
    10,
    { bold: true },
  );
  drawText(formatDate(report.submission_date), width - margin, yPos, 10, { align: "right" });
  yPos += 30;
  drawLine(margin, yPos, width - margin, yPos, 0.5);
  yPos += 20;

  // ── Progress Claim header ──
  drawText(`PROGRESS CLAIM No.${report.claim_number ?? ""}`, margin, yPos, 10, { bold: true });
  drawText(`Payment Due ${formatDate(report.payment_due_date)}`, width / 2 - 40, yPos, 10, {
    bold: true,
  });
  yPos += 40;

  // ── Financial table ──
  const dollarX = 420;
  const amountX = 520;
  const drawFinRow = (label: string, amount: number | string, showDollar = true, isBold = false) => {
    drawText(label, 150, yPos, 10, { bold: isBold });
    if (showDollar) drawText("$", dollarX, yPos, 10, { bold: isBold });
    drawText(fmt(amount), amountX, yPos, 10, { bold: isBold, align: "right" });
    yPos += 22;
  };

  drawFinRow("Contract Value as per breakdown", contractValue, true, true);
  yPos += 10;
  drawFinRow("Variations this month", variationsThisMonth > 0 ? variationsThisMonth : "-");
  drawFinRow("Variations outstanding", variationsOutstanding > 0 ? variationsOutstanding : "-");
  if (installHireThisMonth > 0) {
    drawFinRow("Install & Hire", installHireThisMonth);
  }
  drawFinRow("Claim this month (Ex GST)", claimThisMonth, true, true);
  drawFinRow("Retention Held", retentionAmount > 0 ? -retentionAmount : "-");
  drawFinRow("Subtotal Ex Retention Held", subtotalExRetentionHeld, true, true);
  if (retentionReleaseAmount > 0) {
    drawFinRow("Retention Release", retentionReleaseAmount, true, false);
    drawFinRow("Subtotal Inc Retention Release", subtotalIncRetentionRelease, true, true);
  }
  drawFinRow("GST (10%)", gstAmount, false, false);
  drawFinRow("Subtotal Including GST", subtotalIncGst, true, false);

  drawLine(dollarX - 10, yPos - 5, amountX, yPos - 5, 1);
  drawLine(dollarX - 10, yPos - 2, amountX, yPos - 2, 1);
  yPos += 10;
  drawFinRow("Total Payable", totalPayable, true, true);
  yPos += 30;

  drawText(
    "This is a progress claim made under the Building Industry Fairness (Security of Payment) Act 2017 (QLD)",
    margin,
    yPos,
    9,
    { bold: true },
  );
  yPos += 30;

  // ── Bank details box ──
  const boxHeight = 80;
  const boxY = height - yPos - boxHeight;
  page.drawRectangle({
    x: margin,
    y: boxY,
    width: width - 2 * margin,
    height: boxHeight,
    color: rgb(0.85, 0.93, 0.85),
    borderColor: rgb(0, 0, 0),
    borderWidth: 0.5,
  });

  const bankName = cfg.bank_name || "Commonwealth Bank";
  const bsb = cfg.bank_bsb || "064 - 474";
  const account = cfg.bank_account || "10563659";
  const accountName = cfg.bank_account_name || "SEQ Formwork Pty Ltd";
  let bankY = yPos + 15;
  const payLabel = "Please pay all progress claims directly into our bank account";
  drawText(payLabel, margin + 10, bankY, 9, { bold: true });
  drawLine(margin + 10, bankY + 2, margin + 10 + fontBold.widthOfTextAtSize(payLabel, 9), bankY + 2, 0.5);
  bankY += 18;
  drawText("Bank", margin + 10, bankY, 9, { bold: true });
  drawText(bankName, margin + 60, bankY, 9, { bold: true });
  bankY += 12;
  drawText("BSB", margin + 10, bankY, 9, { bold: true });
  drawText(bsb, margin + 60, bankY, 9, { bold: true });
  bankY += 12;
  drawText("Account", margin + 10, bankY, 9, { bold: true });
  drawText(account, margin + 60, bankY, 9, { bold: true });
  bankY += 12;
  drawText("Name", margin + 10, bankY, 9, { bold: true });
  drawText(accountName, margin + 60, bankY, 9, { bold: true });
  }

  // ── Keep all remaining pages of the existing PDF (attachments) ──
  if (existingPages.length > 1) {
    const copied = await finalDoc.copyPages(existingDoc, existingPages.slice(1));
    copied.forEach((p) => finalDoc.addPage(p));
  }

  // ── Upload the rebuilt PDF; mirror the Send Report storage path scheme ──
  const mergedBytes = await finalDoc.save();
  const monthFolder = `${report.claim_year}-${String(report.claim_month).padStart(2, "0")}`;
  // Storage keys must never contain path separators, whatever the stored filename holds.
  const baseName = (report.pdf_filename || `Claim_${report.claim_month}_${report.claim_year}.pdf`)
    .replace(/\.pdf$/i, "")
    .replace(/[/\\]|\.\./g, "_");
  const path = `${report.project_id}/${monthFolder}/${baseName}_edited_v${Date.now()}.pdf`;

  const { error: uploadErr } = await supabase.storage
    .from("progress-claim-pdfs")
    .upload(path, mergedBytes, { contentType: "application/pdf", upsert: true });
  if (uploadErr) throw uploadErr;

  const { data: urlData } = supabase.storage.from("progress-claim-pdfs").getPublicUrl(path);
  if (!urlData?.publicUrl) throw new Error("Failed to resolve regenerated PDF URL");

  return { pdfUrl: urlData.publicUrl, pageCount: finalDoc.getPageCount() };
}
